// react
import { ReactNode, useRef, useState } from 'react'

// external libs
import { Box, Button, makeStyles, Paper, Typography } from '@material-ui/core'
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd'

// components
import Dialog from '../../../control/dialog/dialog'

// hooks
import useChecklist from '../../../services/um/useChecklist'
import useLoading from '../../../control/loading/useLoading'
import useSnackbarAction from '../../../stores/snackbar/snackbarAction'
import useTranslate from '../../../i18n/useTranslate'

// types
import type { DialogType } from '../../../control/dialog/dialog'
import type { Checklist, ChecklistAnswer, SubChecklist } from '../../../services/um/useChecklist'

export interface OrderedChecklistProps {
    topicID: number
    initialValues: Checklist[]
    onCancel?: () => void
    onOrdered?: () => void
}

enum DroppableType {
    ROOT = 'root',
    CHECKLIST = 'checklist',
    SUB_CHECKLIST = 'subChecklist',
}

enum DraggableType {
    CHECKLIST = 'checklist',
    SUB_CHECKLIST = 'subChecklist',
    CHECKLIST_ANSWER = 'checklistAnswer',
}

function OrderedChecklist(props: OrderedChecklistProps) {
    const { topicID, initialValues, onCancel, onOrdered } = props
    const translate = useTranslate()
    const loadingHook = useLoading()
    const snackbarAction = useSnackbarAction()
    const checklistServices = useChecklist()
    const currentLanguage = translate.languageStore.currentLanguage
    const classes = useStyles()

    const dialogRef = useRef<Record<string, Function>>()
    const [dialogInfo, setDialogInfo] = useState<Partial<DialogType>>()

    const [checklistList, setChecklistList] = useState<Checklist[]>(initialValues)

    function reorder<T>(list: T[], startIndex: number, endIndex: number) {
        const result = Array.from(list)
        const [removed] = result.splice(startIndex, 1)
        result.splice(endIndex, 0, removed)
        return result
    }

    function onDragEnd(result: DropResult) {
        if (!result.destination || result.destination.droppableId !== result.source.droppableId) return

        let updatedChecklistList = Array.from(checklistList)

        const [droppableType] = result.destination.droppableId.split('-')
        const [draggableType, parentID, childrenID] = result.draggableId.split('-')

        switch (droppableType) {
            case DroppableType.ROOT: {
                updatedChecklistList = reorder<Checklist>(
                    updatedChecklistList,
                    result.source.index,
                    result.destination.index,
                )
                break
            }

            case DroppableType.CHECKLIST: {
                const parentIndex = updatedChecklistList.findIndex((e) => e.checklistID === Number(parentID))
                if (draggableType === DraggableType.SUB_CHECKLIST) {
                    updatedChecklistList[parentIndex]!.children = reorder<SubChecklist>(
                        updatedChecklistList[parentIndex]!.children ?? [],
                        result.source.index,
                        result.destination.index,
                    )
                }
                if (draggableType === DraggableType.CHECKLIST_ANSWER) {
                    updatedChecklistList[parentIndex]!.answers = reorder<ChecklistAnswer>(
                        updatedChecklistList[parentIndex]!.answers ?? [],
                        result.source.index,
                        result.destination.index,
                    )
                }
                break
            }

            case DroppableType.SUB_CHECKLIST: {
                const parentIndex = updatedChecklistList.findIndex((e) => e.checklistID === Number(parentID))
                const childrenIndex = updatedChecklistList[parentIndex]!.children!.findIndex(
                    (e) => e.checklistID === Number(childrenID),
                )
                updatedChecklistList[parentIndex]!.children![childrenIndex]!.answers = reorder<ChecklistAnswer>(
                    updatedChecklistList[parentIndex]!.children![childrenIndex]!.answers ?? [],
                    result.source.index,
                    result.destination.index,
                )
                break
            }

            default:
                break
        }

        setChecklistList(updatedChecklistList)
    }

    function normalizeChecklistAnswers(checklistList: Checklist[]) {
        const updatedChecklistList = Array.from(checklistList)
        const normalizeAnswers = (answers?: ChecklistAnswer[] | null) => {
            if (!answers || answers.length === 0) return
            answers.forEach((answer, index) => (answer.isDefault = index === 0))
        }

        for (const checklist of updatedChecklistList) {
            normalizeAnswers(checklist.answers)

            if (checklist.children) {
                for (const subChecklist of checklist.children) {
                    normalizeAnswers(subChecklist.answers)
                }
            }
        }

        return updatedChecklistList
    }

    async function orderChecklist() {
        try {
            loadingHook.loadingOpen()

            // Ensure that only the first answer has isDefault = true
            const updatedChecklistList = normalizeChecklistAnswers(checklistList)

            const { status } = await checklistServices.orderChecklist(topicID, updatedChecklistList)

            if (status === 1) {
                snackbarAction.openSnackbar({
                    message: `${translate.get('UM.SETTING_CONSIDERATION.MESSAGE.SUCCESS')}`,
                    type: 'success',
                })
                dialogRef.current?.openDialog()

                onOrdered && onOrdered()
            }
        } catch (error) {
            console.error('orderChecklist error: ', error)
        } finally {
            loadingHook.loadingClose()
        }
    }

    function onSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault()
        setDialogInfo({
            title: `${translate.get('UM.SETTING_CONSIDERATION.MESSAGE.HEADER')}`,
            content: `${translate.get('UM.SETTING_CONSIDERATION.MESSAGE.CONFIRM')}`,
            type: 'alert-confirm',
            onClickConfirm: () => {
                dialogRef.current?.closeDialog()
                orderChecklist()
            },
        })

        dialogRef.current?.openDialog()
    }
    return (
        <>
            <form onSubmit={onSubmit}>
                <Box display="flex" mt={2} mb={2} justifyContent="flex-end" style={{ gap: 16 }}>
                    <Button className={classes.cancelButton} size="large" variant="contained" onClick={onCancel}>
                        {translate.get('BUTTON.CANCEL')}
                    </Button>
                    <Button type="submit" size="large" variant="contained" color="primary">
                        {translate.get('BUTTON.SAVE')}
                    </Button>
                </Box>

                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="root">
                        {(provided): JSX.Element => (
                            <ul {...provided.droppableProps} ref={provided.innerRef} className={classes.dropableList}>
                                {checklistList.map((checklist, index) => (
                                    <Draggable
                                        key={checklist.checklistID}
                                        draggableId={`${DraggableType.CHECKLIST}-${checklist.checklistID}`}
                                        index={index}
                                    >
                                        {(provided) => (
                                            <li
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                            >
                                                <Paper className={classes.paper} variant="outlined">
                                                    <Paper className={classes.paper} variant="outlined">
                                                        <Typography
                                                            className={classes.headline}
                                                            noWrap
                                                            variant={'body2'}
                                                        >
                                                            {translate.get('LABEL.MAIN_TOPIC')}
                                                        </Typography>
                                                        <Typography className={classes.checklistName} variant={'body2'}>
                                                            {
                                                                checklist[
                                                                    `name${currentLanguage.toUpperCase()}` as keyof Checklist
                                                                ]
                                                            }
                                                        </Typography>
                                                    </Paper>

                                                    {checklist.children && checklist.children.length ? (
                                                        <Box pl={10} mt={2}>
                                                            <OrderedSubChecklist
                                                                parentID={checklist.checklistID}
                                                                subChecklistList={checklist.children}
                                                            />
                                                        </Box>
                                                    ) : null}

                                                    {checklist.answers && checklist.answers.length ? (
                                                        <Box pl={10} mt={2}>
                                                            <OrderedChecklistAnswer
                                                                parentID={checklist.checklistID}
                                                                checklistAnswerList={checklist.answers}
                                                            />
                                                        </Box>
                                                    ) : null}
                                                </Paper>
                                            </li>
                                        )}
                                    </Draggable>
                                ))}

                                {provided.placeholder}
                            </ul>
                        )}
                    </Droppable>
                </DragDropContext>
            </form>

            <Dialog onRef={(funcs) => (dialogRef.current = funcs)} {...dialogInfo} />
        </>
    )
}

interface OrderedSubChecklistProps {
    parentID: number
    subChecklistList: SubChecklist[]
}

function OrderedSubChecklist(props: OrderedSubChecklistProps) {
    const { parentID, subChecklistList } = props
    const translate = useTranslate()
    const classes = useStyles()
    const currentLanguage = translate.languageStore.currentLanguage

    function wrapWithPaperIfHasAnswers(subChecklist: SubChecklist, content: ReactNode) {
        return subChecklist.answers && subChecklist.answers.length ? (
            <Paper className={classes.paper} variant="outlined">
                {content}
            </Paper>
        ) : (
            content
        )
    }

    return (
        <Droppable droppableId={`${DroppableType.CHECKLIST}-${parentID}`} type="SUB_CHECKLIST">
            {(provided): JSX.Element => (
                <ul {...provided.droppableProps} ref={provided.innerRef} className={classes.dropableList}>
                    {subChecklistList.map((subChecklist, index) => (
                        <Draggable
                            key={subChecklist.checklistID}
                            draggableId={`${DraggableType.SUB_CHECKLIST}-${parentID}-${subChecklist.checklistID}`}
                            index={index}
                        >
                            {(provided) => (
                                <li ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                    {wrapWithPaperIfHasAnswers(
                                        subChecklist,
                                        <>
                                            <Paper className={classes.paper} variant="outlined">
                                                <Typography className={classes.headline} noWrap variant={'body2'}>
                                                    {translate.get('LABEL.SUB_TOPIC')}
                                                </Typography>
                                                <Typography className={classes.checklistName} variant={'body2'}>
                                                    {
                                                        subChecklist[
                                                            `name${currentLanguage.toUpperCase()}` as keyof SubChecklist
                                                        ]
                                                    }
                                                </Typography>
                                            </Paper>

                                            {subChecklist.answers && subChecklist.answers.length ? (
                                                <Box pl={10} mt={2}>
                                                    <OrderedChecklistAnswer
                                                        parentID={parentID}
                                                        childrenID={subChecklist.checklistID}
                                                        checklistAnswerList={subChecklist.answers}
                                                    />
                                                </Box>
                                            ) : null}
                                        </>,
                                    )}
                                </li>
                            )}
                        </Draggable>
                    ))}

                    {provided.placeholder}
                </ul>
            )}
        </Droppable>
    )
}
interface OrderedChecklistAnswerProps {
    parentID: number
    childrenID?: number | undefined
    checklistAnswerList: ChecklistAnswer[]
}

function OrderedChecklistAnswer(props: OrderedChecklistAnswerProps) {
    const { childrenID, parentID, checklistAnswerList } = props
    const translate = useTranslate()
    const classes = useStyles()
    const currentLanguage = translate.languageStore.currentLanguage

    const droppableId = childrenID
        ? `${DroppableType.SUB_CHECKLIST}-${childrenID}`
        : `${DroppableType.CHECKLIST}-${parentID}`

    function getDraggableId(checklistID: number) {
        return `${DraggableType.CHECKLIST_ANSWER}-${parentID}${childrenID ? `-${childrenID}` : ''}-${checklistID}`
    }

    return (
        <Droppable droppableId={droppableId} type="ANSWER">
            {(provided): JSX.Element => (
                <ul {...provided.droppableProps} ref={provided.innerRef} className={classes.dropableList}>
                    {checklistAnswerList.map((checklistAnswer, index) => (
                        <Draggable
                            key={checklistAnswer.checklistID}
                            draggableId={getDraggableId(checklistAnswer.checklistID)}
                            index={index}
                        >
                            {(provided) => (
                                <li ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                    <Paper className={classes.paper} variant="outlined">
                                        <Typography className={classes.headline} noWrap variant={'body2'}>
                                            {translate.get('LABEL.TOPIC_ANSWER')}
                                        </Typography>
                                        <Typography className={classes.checklistName} variant={'body2'}>
                                            {
                                                checklistAnswer[
                                                    `name${currentLanguage.toUpperCase()}` as keyof ChecklistAnswer
                                                ]
                                            }
                                        </Typography>
                                    </Paper>
                                </li>
                            )}
                        </Draggable>
                    ))}

                    {provided.placeholder}
                </ul>
            )}
        </Droppable>
    )
}

const useStyles = makeStyles((theme) => ({
    cancelButton: {
        backgroundColor: '#FFFFFF',
        border: 'solid 1px #db5f8e',
    },

    paper: {
        padding: 24,
        transition: '0.3s',
        borderRadius: '4px',
        position: 'relative',
        '&:hover': {
            border: `1px solid ${theme.palette.primary.main}`,
        },
    },

    headline: {
        border: 'unset',
        fontWeight: 'bolder',
        fontSize: '16px',
        color: theme.palette.grey[50],
        padding: '5px',
    },

    checklistName: {
        fontWeight: 'lighter',
        fontSize: '20px',
        padding: '5px',
    },

    dropableList: {
        listStyle: 'none',
        paddingLeft: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
}))

export default OrderedChecklist
